import { Color3, Color4, Material, Matrix, Mesh, MeshBuilder, PBRMaterial, Quaternion, Scene, StandardMaterial, TransformNode, Vector3, VertexData } from "@babylonjs/core";
import type { GameAssetManager } from "../core/AssetManager";

export const TREE_ASSET_PATH = "./assets/environment/tree_low_poly_lowpoly.glb";
export const JUNGLE_CONFIG = { mapWidth: 68, mapDepth: 68, playableHalfExtent: 28, spawnClearance: 3.2, ghostSpawnDistance: { min: 5, max: 26 } } as const;

export interface JungleObstacle { readonly position: Vector3; readonly radius: number; readonly kind?: "tree" | "rock"; }
export interface JungleData {
    readonly ground: Mesh;
    readonly obstacles: readonly JungleObstacle[];
    readonly waypoints: readonly Vector3[];
    readonly trees: readonly TransformNode[];
    readonly visualReady: Promise<boolean>;
}

export interface PlantPlacement { readonly position: Vector3; readonly rotation: number; readonly scale: number; }
export type GroundVegetationKind = "grass" | "fern" | "bush";
export interface GroundVegetationPlacement extends PlantPlacement { readonly kind: GroundVegetationKind; }
export const FOREST_PLAYER_SPAWN = new Vector3(0, 1.65, 14);
export function junglePathCenter(z: number): number { return Math.sin((z + 3) * 0.16) * 2.15 + Math.sin(z * 0.055) * 0.75; }
export function jungleTerrainHeightAt(_x: number, _z: number): number { return -0.04; }
function seededGenerator(seed = 0x50454e41): () => number { let state = seed; return () => ((state = Math.imul(state ^ (state >>> 15), 1 | state)) >>> 0) / 4294967296; }
export function generateTreePlacements(count: number): PlantPlacement[] {
    const random = seededGenerator(); const placements: PlantPlacement[] = [
        { position: new Vector3(-3.4, 0, 10.8), rotation: .4, scale: 1.05 }, { position: new Vector3(5.4, 0, 9.3), rotation: 1.7, scale: .92 },
        { position: new Vector3(-4.2, 0, 16.9), rotation: 2.6, scale: 1.18 }, { position: new Vector3(4.6, 0, 18.2), rotation: 4.2, scale: .86 },
        { position: new Vector3(-3.1, 0, 5.4), rotation: 5.1, scale: .98 }, { position: new Vector3(5.7, 0, 3.1), rotation: 3.4, scale: 1.12 },
    ].slice(0, count); let attempts = 0;
    while (placements.length < count && attempts++ < count * 80) {
        const index = placements.length; let x: number, z: number;
        if (index % 5 === 0) { const edge = JUNGLE_CONFIG.playableHalfExtent + 1.2 + random() * 3.5; if (random() < .5) { x = (random() < .5 ? -1 : 1) * edge; z = (random() - .5) * (JUNGLE_CONFIG.mapDepth - 4); } else { z = (random() < .5 ? -1 : 1) * edge; x = (random() - .5) * (JUNGLE_CONFIG.mapWidth - 4); } }
        else { const angle = random() * Math.PI * 2; const band = index % 4 === 1 ? 3 + random() * 9 : index % 4 === 2 ? 9 + random() * 12 : 14 + random() * 18; x = Math.cos(angle) * band + (random() - .5) * 3; z = Math.sin(angle) * band + (random() - .5) * 3; }
        x = Math.max(-32.5, Math.min(32.5, x)); z = Math.max(-32.5, Math.min(32.5, z));
        if (Math.hypot(x - FOREST_PLAYER_SPAWN.x, z - FOREST_PLAYER_SPAWN.z) < JUNGLE_CONFIG.spawnClearance) continue;
        if (Math.abs(x - junglePathCenter(z)) < 2.15 && Math.abs(z) < 29) continue;
        if (placements.some((placement) => Vector3.DistanceSquared(placement.position, new Vector3(x, 0, z)) < 1.35 ** 2)) continue;
        placements.push({ position: new Vector3(x, 0, z), rotation: random() * Math.PI * 2, scale: .72 + random() * .58 });
    }
    return placements;
}
const TREE_PLACEMENTS = generateTreePlacements(160);

export const FOREST_NAV_OBSTACLES: readonly JungleObstacle[] = [
    ...TREE_PLACEMENTS.map((tree) => ({ position: tree.position.clone(), radius: 0.68 * tree.scale, kind: "tree" as const })),
];
export interface VegetationDensity { readonly trees: number; readonly grass: number; readonly ferns: number; readonly bushes: number; readonly groundCover: number; }
export function getVegetationDensity(quality: "low" | "medium" | "high"): VegetationDensity {
    if (quality === "low") return { trees: 100, grass: 120, ferns: 28, bushes: 12, groundCover: 160 };
    if (quality === "medium") return { trees: 130, grass: 180, ferns: 42, bushes: 18, groundCover: 240 };
    return { trees: 160, grass: 250, ferns: 60, bushes: 30, groundCover: 340 };
}

export function generateRoamingAnchors(obstacles: readonly JungleObstacle[], count = 28): Vector3[] {
    const random = seededGenerator(0x544f594f); const anchors: Vector3[] = []; let attempts = 0;
    while (anchors.length < count && attempts++ < count * 100) {
        const x = -26 + random() * 52; const z = -26 + random() * 52; const candidate = new Vector3(x, 0, z);
        if (Vector3.DistanceSquared(candidate, FOREST_PLAYER_SPAWN) < JUNGLE_CONFIG.spawnClearance ** 2) continue;
        if (obstacles.some((obstacle) => Vector3.DistanceSquared(candidate, obstacle.position) < (obstacle.radius + 1.05) ** 2)) continue;
        if (anchors.some((anchor) => Vector3.DistanceSquared(candidate, anchor) < 3.1 ** 2)) continue;
        anchors.push(candidate);
    }
    return anchors;
}

export function generateGroundVegetationPlacements(density: VegetationDensity, obstacles: readonly JungleObstacle[]): GroundVegetationPlacement[] {
    const random = seededGenerator(0x47524f57); const placements: GroundVegetationPlacement[] = [];
    const add = (kind: GroundVegetationKind, count: number): void => {
        let created = 0; let attempts = 0; while (created < count && attempts++ < count * 60) {
            const nearTree = random() < .58 && obstacles.length > 0; let x: number, z: number;
            if (nearTree) { const tree = obstacles[Math.floor(random() * obstacles.length)]!; const angle = random() * Math.PI * 2; const radius = tree.radius + 0.7 + random() * 2.7; x = tree.position.x + Math.cos(angle) * radius; z = tree.position.z + Math.sin(angle) * radius; }
            else { x = -27 + random() * 54; z = -27 + random() * 54; }
            if (Math.abs(x) > 27 || Math.abs(z) > 27 || Math.hypot(x - FOREST_PLAYER_SPAWN.x, z - FOREST_PLAYER_SPAWN.z) < 2.4) continue;
            if (obstacles.some((obstacle) => Math.hypot(x - obstacle.position.x, z - obstacle.position.z) < obstacle.radius + .26)) continue;
            if (kind === "bush" && placements.some((placement) => placement.kind === "bush" && Math.hypot(x - placement.position.x, z - placement.position.z) < 1.8)) continue;
            placements.push({ kind, position: new Vector3(x, jungleTerrainHeightAt(x, z), z), rotation: random() * Math.PI * 2, scale: kind === "grass" ? .55 + random() * .75 : kind === "fern" ? .7 + random() * .65 : .65 + random() * .8 }); created++;
        }
    };
    add("grass", density.grass); add("fern", density.ferns); add("bush", density.bushes); return placements;
}

function createLeafClusterSource(name: string, kind: "grass" | "fern", scene: Scene): Mesh {
    const positions: number[] = []; const indices: number[] = [];
    const leafCount = kind === "grass" ? 5 : 8;
    for (let leaf = 0; leaf < leafCount; leaf++) {
        const angle = leaf * Math.PI * 2 / leafCount + (kind === "grass" ? (leaf % 2) * .22 : 0); const right = new Vector3(Math.cos(angle), 0, -Math.sin(angle)); const forward = new Vector3(Math.sin(angle), 0, Math.cos(angle));
        const width = kind === "grass" ? .09 : .14; const length = kind === "grass" ? .72 : .68; const base = kind === "grass" ? Vector3.Zero() : forward.scale(.06); const middle = base.add(forward.scale(length * .55)).add(new Vector3(0, kind === "grass" ? length * .55 : .08, 0)); const tip = base.add(forward.scale(length)).add(new Vector3(0, kind === "grass" ? length : .03 + (leaf % 2) * .04, 0));
        const vertices = [base.subtract(right.scale(width)), base.add(right.scale(width)), middle.subtract(right.scale(width * .72)), middle.add(right.scale(width * .72)), tip]; const start = positions.length / 3; for (const vertex of vertices) positions.push(vertex.x, vertex.y, vertex.z); indices.push(start, start + 1, start + 2, start + 1, start + 3, start + 2, start + 2, start + 3, start + 4);
    }
    const normals: number[] = []; VertexData.ComputeNormals(positions, indices, normals); const mesh = new Mesh(name, scene); const data = new VertexData(); data.positions = positions; data.indices = indices; data.normals = normals; data.applyToMesh(mesh); return mesh;
}

export class JungleGenerator {
    constructor(private readonly scene: Scene, private readonly quality: "low" | "medium" | "high", private readonly assets: GameAssetManager) { }

    generate(): JungleData {
        this.scene.clearColor = new Color4(0.006, 0.026, 0.028, 1);
        this.scene.fogMode = Scene.FOGMODE_EXP2;
        this.scene.fogDensity = this.quality === "low" ? 0.011 : this.quality === "medium" ? 0.015 : 0.018;
        this.scene.fogColor = new Color3(0.018, 0.055, 0.052);
        this.scene.environmentIntensity = 0.32;

        const ground = MeshBuilder.CreateGround("jungle-ground", { width: JUNGLE_CONFIG.mapWidth, height: JUNGLE_CONFIG.mapDepth, subdivisions: 8 }, this.scene);
        ground.position.y = -0.04;
        ground.isPickable = true;
        ground.metadata = { ground: true };
        const groundMaterial = new StandardMaterial("jungle-earth", this.scene);
        groundMaterial.diffuseColor = new Color3(0.035, 0.09, 0.04);
        groundMaterial.specularColor = Color3.Black();
        ground.material = groundMaterial;

        const pathCenters = Array.from({ length: 25 }, (_, index) => { const z = -30 + index * 2.5; return new Vector3(junglePathCenter(z), -0.025, z); });
        const left = pathCenters.map((point, index) => { const previous = pathCenters[Math.max(0, index - 1)]!; const next = pathCenters[Math.min(pathCenters.length - 1, index + 1)]!; const tangent = next.subtract(previous).normalize(); return point.add(new Vector3(-tangent.z, 0, tangent.x).scale(3.1)); });
        const right = pathCenters.map((point, index) => { const previous = pathCenters[Math.max(0, index - 1)]!; const next = pathCenters[Math.min(pathCenters.length - 1, index + 1)]!; const tangent = next.subtract(previous).normalize(); return point.add(new Vector3(tangent.z, 0, -tangent.x).scale(3.1)); });
        const path = MeshBuilder.CreateRibbon("jungle-path", { pathArray: [left, right], sideOrientation: Mesh.DOUBLESIDE, closeArray: false, closePath: false }, this.scene);
        path.isPickable = true;
        path.metadata = { ground: true };
        const pathMaterial = new StandardMaterial("jungle-path-earth", this.scene);
        pathMaterial.diffuseColor = new Color3(0.14, 0.1, 0.055);
        pathMaterial.specularColor = Color3.Black();
        path.material = pathMaterial;

        const density = getVegetationDensity(this.quality); const treeCount = density.trees;
        const obstacles: JungleObstacle[] = TREE_PLACEMENTS.slice(0, treeCount).map((tree): JungleObstacle => ({ position: tree.position.clone(), radius: 0.68 * tree.scale, kind: "tree" }));
        this.createCollisionProxies(obstacles);
        this.createFireflies();
        this.createGroundVegetation(generateGroundVegetationPlacements(density, obstacles));

        const waypoints = generateRoamingAnchors(obstacles);
        const trees: TransformNode[] = [];
        const visualReady = this.loadVegetation(treeCount, trees);
        return { ground, obstacles, waypoints, trees, visualReady };
    }

    private async loadVegetation(treeCount: number, trees: TransformNode[]): Promise<boolean> {
        const treeMeshes = ["Tree 1_Tree 1_0", "Tree 2_Tree 1_0"];
        const jobs: Promise<TransformNode | null>[] = [];
        for (let index = 0; index < treeCount; index++) {
            const placement = TREE_PLACEMENTS[index]!;
            jobs.push(this.instantiateTree(index, treeMeshes[index % treeMeshes.length]!, placement));
        }
        const loaded = await Promise.all(jobs);
        if (this.scene.isDisposed) { loaded.forEach((mesh) => mesh?.dispose()); return false; }
        for (const mesh of loaded) if (mesh) trees.push(mesh);
        return loaded.filter(Boolean).length >= treeCount;
    }

    private async instantiateTree(index: number, selectedMeshName: string, placement: PlantPlacement): Promise<TransformNode | null> {
        const root = await this.assets.instantiate(TREE_ASSET_PATH, `jungle-tree-${index}`, {
            scale: (5.3 + (index % 7) * 0.38) * placement.scale,
            normalizeBy: "height",
            rotation: new Vector3(0, placement.rotation, 0),
        });
        if (!root || this.scene.isDisposed) { root?.dispose(); return null; }

        const meshes = root.getChildMeshes(false);
        const selected = meshes.find((mesh) => mesh.name.toLowerCase().includes(selectedMeshName.toLowerCase())) ?? meshes[0];
        if (!selected) { root.dispose(); return null; }
        for (const mesh of meshes) {
            const isAuthoredTreeMesh = mesh.name.includes("Tree 1_Tree 1_0") || mesh.name.includes("Tree 2_Tree 1_0");
            mesh.setEnabled(!isAuthoredTreeMesh || mesh === selected);
            mesh.isPickable = false;
            // Keep visible jungle geometry in the opaque/alpha-test group so its
            // depth is established before translucent mosquito-spray particles.
            // Do not assign the invisible gameplay proxy to this render group.
            mesh.renderingGroupId = 0;
            mesh.metadata = { scenery: true, sourceMesh: selectedMeshName, sharedGeometry: true };

            const material = mesh.material;
            if (mesh.isEnabled() && (material instanceof PBRMaterial || material instanceof StandardMaterial)) {
                // Diagnostic cutout mode: write visible tree pixels to the depth buffer
                // before drawing translucent mosquito-spray particles.
                // Note: alpha-test can make soft foliage edges look sharper.
                material.transparencyMode = Material.MATERIAL_ALPHATEST;
                material.forceDepthWrite = true;
                material.needDepthPrePass = false;
            }

            if (import.meta.env.DEV && index === 0 && mesh.isEnabled() && material) {
                console.info("[Jungle] Tree depth diagnostics", {
                    mesh: mesh.name,
                    renderingGroupId: mesh.renderingGroupId,
                    material: material.name,
                    transparencyMode: material.transparencyMode,
                    alphaBlending: material.needAlphaBlending(),
                    alphaTesting: material.needAlphaTesting(),
                    forceDepthWrite: material.forceDepthWrite,
                    disableDepthWrite: material.disableDepthWrite,
                });
            }
        }

        root.computeWorldMatrix(true);
        selected.computeWorldMatrix(true);
        const bounds = selected.getBoundingInfo().boundingBox;
        root.position.addInPlace(new Vector3(
            placement.position.x - bounds.centerWorld.x,
            placement.position.y - bounds.minimumWorld.y,
            placement.position.z - bounds.centerWorld.z,
        ));
        root.computeWorldMatrix(true);
        return root;
    }

    private createCollisionProxies(obstacles: readonly JungleObstacle[]): void {
        obstacles.forEach((obstacle, index) => {
            const proxy = MeshBuilder.CreateCylinder(`jungle-tree-obstacle-${index}`, { height: 5.2, diameter: obstacle.radius * 2, tessellation: 8 }, this.scene);
            proxy.position.set(obstacle.position.x, 2.55, obstacle.position.z);
            proxy.visibility = 0;
            proxy.isPickable = true;
            proxy.metadata = { worldObstacle: true, obstacleKind: "tree" };
        });
    }

    // private createFirefliesOld(): void {
    //     const count = this.quality === "low" ? 6 : this.quality === "medium" ? 12 : 18;
    //     const material = new StandardMaterial("firefly-glow", this.scene);
    //     material.emissiveColor = new Color3(0.58, 0.82, 0.22);
    //     material.disableLighting = true;
    //     for (let index = 0; index < count; index++) {
    //         const firefly = MeshBuilder.CreateSphere(`firefly-${index}`, { diameter: 0.035, segments: 4 }, this.scene);
    //         firefly.position.set(-12 + (index * 7.3) % 24, 0.8 + (index % 5) * 0.35, -25 + (index * 11.7) % 50);
    //         firefly.material = material;
    //         firefly.isPickable = false;
    //     }
    // }

    private fireflies: Mesh[] = [];
    private createFireflies(): void {
        const count = this.quality === "low" ? 12 : this.quality === "medium" ? 24 : 40;
        const material = new StandardMaterial("firefly-glow", this.scene);
        material.emissiveColor = new Color3(0.58, 0.82, 0.22);
        material.disableLighting = true;

        for (let index = 0; index < count; index++) {
            // Reduced diameter from 0.035 to 0.015 for smaller size
            const firefly = MeshBuilder.CreateSphere(`firefly-${index}`, { diameter: 0.015, segments: 4 }, this.scene);

            firefly.position.set(-12 + (index * 4.3) % 24, 0.8 + (index % 7) * 0.25, -25 + (index * 7.7) % 50);
            firefly.material = material;
            firefly.isPickable = false;

            this.fireflies.push(firefly);
        }

        // Animation loop for floating movement
        let time = 0;
        this.scene.onBeforeRenderObservable.add(() => {
            time += 0.01; // Movement speed

            this.fireflies.forEach((firefly, index) => {
                // Unique offset so they don't move in perfect sync
                const offset = index * 2.0;

                // Subtle random drifting using sine waves
                firefly.position.x += Math.sin(time + offset) * 0.005;
                firefly.position.y += Math.cos(time * 1.5 + offset) * 0.003;
                firefly.position.z += Math.sin(time * 0.8 + offset) * 0.005;
            });
        });
    }

    private createGroundVegetation(placements: readonly GroundVegetationPlacement[]): void {
        const makeMaterial = (name: string, color: Color3): StandardMaterial => { const material = new StandardMaterial(name, this.scene); material.diffuseColor = color; material.specularColor = Color3.Black(); material.backFaceCulling = false; return material; };
        const grass = createLeafClusterSource("tropical-grass-source", "grass", this.scene); grass.material = makeMaterial("tropical-grass-material", new Color3(.055, .24, .07));
        const fern = createLeafClusterSource("tropical-fern-source", "fern", this.scene); fern.material = makeMaterial("tropical-fern-material", new Color3(.045, .29, .095));
        const bush = MeshBuilder.CreatePolyhedron("tropical-bush-source", { type: 2, size: .62 }, this.scene); bush.material = makeMaterial("tropical-bush-material", new Color3(.035, .19, .055));
        const sources: Record<GroundVegetationKind, Mesh> = { grass, fern, bush }; const matrices: Record<GroundVegetationKind, Matrix[]> = { grass: [], fern: [], bush: [] };
        for (const placement of placements) { const y = placement.position.y + (placement.kind === "bush" ? .34 * placement.scale : .012); const scaling = placement.kind === "grass" ? new Vector3(.8 + placement.scale * .25, placement.scale, .8 + placement.scale * .25) : placement.kind === "fern" ? new Vector3(placement.scale, placement.scale * .82, placement.scale) : new Vector3(placement.scale * 1.15, placement.scale * .72, placement.scale); const rotation = Quaternion.FromEulerAngles(0, placement.rotation, placement.kind === "grass" ? (placement.rotation % 0.18) - .09 : 0); matrices[placement.kind].push(Matrix.Compose(scaling, rotation, new Vector3(placement.position.x, y, placement.position.z))); }
        for (const kind of ["grass", "fern", "bush"] as const) { const source = sources[kind]; source.isPickable = false; source.checkCollisions = false; source.metadata = { groundVegetation: true, kind, visualOnly: true }; source.thinInstanceAdd(matrices[kind]); source.alwaysSelectAsActiveMesh = true; }
    }
}
